import React from 'react';
import { useRoutes, Navigate } from 'react-router-dom';
import {
  BlankRoutes,
  ErrorRoutes,
  OtherRoutes,
  FormElementRoutes,
  AuthRoutes,
  AssetsRoutes,
  InventoryRoutes,
  VendorRoutes,
  GateRegisterRoutes,
  DashboardRoutes,
  MasterWarehouseRoutes,
  GateRoutes,
  UserRoutes,
  CourierPartnerRoutes,
} from '@/Route/AuthRoutes';
import Layout from '@/Layout';
import { ProtectedRoute } from '@/Shared';
import ROLES from '../Constants/roles';
// ErrorRoutes
const BadRequest = React.lazy(() => import('@/Pages/ErrorPages/BadRequest'));
const Forbidden = React.lazy(() => import('@/Pages/ErrorPages/Forbidden'));
const NotFound = React.lazy(() => import('@/Pages/ErrorPages/NotFound'));
const InternalServer = React.lazy(() => import('@/Pages/ErrorPages/InternalServer'));
const ServiceUnavailable = React.lazy(() => import('@/Pages/ErrorPages/ServiceUnavailable'));

// OtherRoutes
const Blank = React.lazy(() => import('@/Pages/OtherPage/Blank'));
const Maintenance = React.lazy(() => import('@/Pages/OtherPage/Maintenance'));
const ComingSoon = React.lazy(() => import('@/Pages/OtherPage/ComingSoon'));
const Sitemap = React.lazy(() => import('@/Pages/OtherPage/Sitemap'));
const PrivacyPolicy = React.lazy(() => import('@/Pages/OtherPage/PrivacyPolicy'));
const TermsCondition = React.lazy(() => import('@/Pages/OtherPage/TermsCondition'));
const FormValidation = React.lazy(() => import('@/Pages/FormElements/FormValidation'));
const BaseInput = React.lazy(() => import('@/Pages/FormElements/BaseInput'));
const InputGroups = React.lazy(() => import('@/Pages/FormElements/InputGroups'));
const InputMasks = React.lazy(() => import('@/Pages/FormElements/InputMasks'));
const FloatingLabels = React.lazy(() => import('@/Pages/FormElements/FloatingLabels'));
const Datetimepicker = React.lazy(() => import('@/Pages/FormElements/Datetimepicker'));
const TouchSpin = React.lazy(() => import('@/Pages/FormElements/TouchSpin'));
const FormSelect = React.lazy(() => import('@/Pages/FormElements/FormSelect'));
const Switch = React.lazy(() => import('@/Pages/FormElements/Switch'));
const FormTextarea = React.lazy(() => import('@/Pages/FormElements/FormTextarea'));
const Typeahead = React.lazy(() => import('@/Pages/FormElements/Typeahead'));
const FormClipboard = React.lazy(() => import('@/Pages/FormElements/FormClipboard'));
const FIleUpload = React.lazy(() => import('@/Pages/FormElements/FileUpload'));
const DefaultForms = React.lazy(() => import('@/Pages/FormElements/DefaultForms'));
const OauthVerify = React.lazy(() => import('@/Pages/AuthPages/OauthSignIn'));

// Assets
const Assets = React.lazy(() => import('@/Pages/Assets'));
const AssetsList = React.lazy(() => import('@/Pages/Assets/AssetsList'));
const AddAssets = React.lazy(() => import('@/Pages/Assets/CreateAssets'));

// Inventory
const InventoryList = React.lazy(() => import('@/Pages/Inventory/InventoryList'));
const EditInventory = React.lazy(() => import('@/Pages/Inventory/EditInventory'));
const AddInventory = React.lazy(() => import('@/Pages/Inventory/AddInventory'));
const ManageInventory = React.lazy(() => import('@/Pages/Inventory/ManageInventory'));
const StockTransfer = React.lazy(() => import('@/Pages/Inventory/StockTransfer'));
const InventoryTransfer = React.lazy(() => import('@/Pages/Inventory/AssetTransfer'));
const InventoryTransferList = React.lazy(() => import('@/Pages/Inventory/AssetTransferList'));
const InventoryTransferView = React.lazy(() => import('@/Pages/Inventory/AssetTransferView'));
const AssetShipmentView = React.lazy(() => import('@/Pages/Inventory/AssetShipmentView'));
const CaseList = React.lazy(() => import('@/Pages/Inventory/Cases/List'));
const CaseView = React.lazy(() => import('@/Pages/Inventory/Cases/View'));
// Vendor
const EditVendor = React.lazy(() => import('@/Pages/Vendor/EditVendor'));
const AddVendor = React.lazy(() => import('@/Pages/Vendor/AddVendor'));
const VendorList = React.lazy(() => import('@/Pages/Vendor/VendorList'));

// Courier Partner
const CourierPartnerList = React.lazy(() => import('@/Pages/CourierPartner/CourierPartnerList'));

// Dashboard
const Dashboard = React.lazy(() => import('@/Pages/Dashboard'));

// Gate Register
const PersonRegisterList = React.lazy(() => import('@/Pages/GateRegister/PersonRegisterList'));
const VehicleRegisterList = React.lazy(() => import('@/Pages/GateRegister/VehicleRegisterList'));
const PersonRegister = React.lazy(() => import('@/Pages/GateKeeper/Person/Register'));
const ThanksPersonRegister = React.lazy(() => import('@/Pages/GateKeeper/Person/Register/ThanksPersonRegister'));

// Master Warehouse
const MasterWarehouseList = React.lazy(() => import('@/Pages/MasterWarehouse/MasterWarehouseList'));
const AddMasterWarehouse = React.lazy(() => import('@/Pages/MasterWarehouse/AddMasterWarehouse'));
const ViewMasterWarehouse = React.lazy(() => import('@/Pages/MasterWarehouse/ViewMasterWarehouse'));
const EditMasterWarehouse = React.lazy(() => import('@/Pages/MasterWarehouse/EditMasterWarehouse'));

// Gate
const GateList = React.lazy(() => import('@/Pages/Gate/GateList'));
const AddGate = React.lazy(() => import('@/Pages/Gate/AddGate'));
const ViewGate = React.lazy(() => import('@/Pages/Gate/ViewGate'));
const EditGate = React.lazy(() => import('@/Pages/Gate/EditGate'));

// User
const UserList = React.lazy(() => import('@/Pages/User/UserList'));
const AddUser = React.lazy(() => import('@/Pages/User/AddUser'));
const ViewUser = React.lazy(() => import('@/Pages/User/ViewUser'));
const EditUser = React.lazy(() => import('@/Pages/User/EditUser'));

const Routes = () => {
  let element = [
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      ),
      children: [
        { path: '/', element: <Navigate to={DashboardRoutes.DASHBOARD} replace /> },
        { path: DashboardRoutes.DASHBOARD, element: <Dashboard /> },
        // OtherRoutes
        { path: OtherRoutes.OTHER_BLANK_PAGE, element: <Blank /> },

        { path: OtherRoutes.SITE_MAP, element: <Sitemap /> },
        { path: OtherRoutes.PRIVACY_POLICY, element: <PrivacyPolicy /> },
        { path: OtherRoutes.TERMS_CONDITION, element: <TermsCondition /> },

        // BlankRoutes
        { path: BlankRoutes.ANOTHER_BLANK_PAGE, element: <Blank /> },

        // FormElement
        { path: FormElementRoutes.FORM_VALIDATION, element: <FormValidation /> },
        { path: FormElementRoutes.BASE_INPUT, element: <BaseInput /> },
        { path: FormElementRoutes.INPUT_GROUPS, element: <InputGroups /> },
        { path: FormElementRoutes.INPUT_MASKS, element: <InputMasks /> },
        { path: FormElementRoutes.FLOATING_LABEL, element: <FloatingLabels /> },
        { path: FormElementRoutes.DATE_TIME_PICKER, element: <Datetimepicker /> },
        { path: FormElementRoutes.TOUCH_SPINE, element: <TouchSpin /> },
        { path: FormElementRoutes.FORM_SELECT, element: <FormSelect /> },
        { path: FormElementRoutes.SWITCH, element: <Switch /> },
        { path: FormElementRoutes.TYPEAHEAD, element: <Typeahead /> },
        { path: FormElementRoutes.FORM_TEXTAREA, element: <FormTextarea /> },
        { path: FormElementRoutes.FORM_CLIPBOARD, element: <FormClipboard /> },
        { path: FormElementRoutes.FILE_UPLOAD, element: <FIleUpload /> },
        { path: FormElementRoutes.DEFAULT_FORMS, element: <DefaultForms /> },

        // Assets
        // { path: AssetsRoutes.ASSETS, element: <Assets /> },
        {
          path: AssetsRoutes.ADD_ASSETS,
          element: (
            <ProtectedRoute roles={[ROLES.superAdmin]}>
              <AddAssets />
            </ProtectedRoute>
          ),
        },
        {
          path: AssetsRoutes.EDIT_ASSETS,
          element: (
            <ProtectedRoute roles={[ROLES.superAdmin]}>
              <AddAssets />
            </ProtectedRoute>
          ),
        },
        { path: AssetsRoutes.ASSETS_LIST, element: <AssetsList /> },

        // Inventory
        { path: InventoryRoutes.ADD_INVENTORY, element: <AddInventory /> },
        { path: InventoryRoutes.INVENTORY_LIST, element: <InventoryList /> },
        { path: InventoryRoutes.EDIT_INVENTORY, element: <EditInventory /> },
        { path: InventoryRoutes.MANAGE_INVENTORY, element: <ManageInventory /> },
        { path: InventoryRoutes.STOCK_TRANSFER, element: <StockTransfer /> },
        { path: InventoryRoutes.INVENTORY_TRANSFER, element: <InventoryTransferList /> },
        { path: InventoryRoutes.ADD_INVENTORY_TRANSFER, element: <InventoryTransfer /> },
        { path: InventoryRoutes.VIEW_INVENTORY_TRANSFER, element: <InventoryTransferView /> },
        { path: InventoryRoutes.VIEW_ASSET_SHIPMENT, element: <AssetShipmentView /> },
        { path: InventoryRoutes.INVENTORY_CASES, element: <CaseList /> },
        { path: InventoryRoutes.VIEW_INVENTORY_CASE, element: <CaseView /> },

        // Vendor
        {
          path: VendorRoutes.ADD_VENDOR,
          element: (
            <ProtectedRoute roles={[ROLES.superAdmin]}>
              <AddVendor />
            </ProtectedRoute>
          ),
        },
        { path: VendorRoutes.VENDOR_LIST, element: <VendorList /> },
        {
          path: VendorRoutes.EDIT_VENDOR,
          element: (
            <ProtectedRoute roles={[ROLES.superAdmin]}>
              <EditVendor />
            </ProtectedRoute>
          ),
        },

        // Courier Partner
        { path: CourierPartnerRoutes.COURIER_PARTNER_LIST, element: <CourierPartnerList /> },

        // Gate Register
        { path: GateRegisterRoutes.PERSON_REGISTER, element: <PersonRegisterList /> },
        { path: GateRegisterRoutes.VEHICLE_REGISTER, element: <VehicleRegisterList /> },

        // Master Warehouse
        { path: MasterWarehouseRoutes.MASTER_WAREHOUSE_LIST, element: <MasterWarehouseList /> },
        { path: MasterWarehouseRoutes.ADD_MASTER_WAREHOUSE, element: <AddMasterWarehouse /> },
        { path: MasterWarehouseRoutes.VIEW_MASTER_WAREHOUSE, element: <ViewMasterWarehouse /> },
        { path: MasterWarehouseRoutes.EDIT_MASTER_WAREHOUSE, element: <EditMasterWarehouse /> },

        // Gate
        { path: GateRoutes.GATE_LIST, element: <GateList /> },
        { path: GateRoutes.ADD_GATE, element: <AddGate /> },
        { path: GateRoutes.VIEW_GATE, element: <ViewGate /> },
        { path: GateRoutes.EDIT_GATE, element: <EditGate /> },

        // User
        { path: UserRoutes.USER_LIST, element: <UserList /> },
        { path: UserRoutes.ADD_USER, element: <AddUser /> },
        { path: UserRoutes.VIEW_USER, element: <ViewUser /> },
        { path: UserRoutes.EDIT_USER, element: <EditUser /> },
      ],
    },
    // / Person Register Open route
    { path: GateRegisterRoutes.PERSON_OPEN_REGISTER, element: <PersonRegister /> },
    { path: GateRegisterRoutes.PERSON_REGISTER_THANKS, element: <ThanksPersonRegister /> },
    // / ErrorRotes
    { path: AuthRoutes.OAUTH_SIGN, element: <OauthVerify /> },
    { path: ErrorRoutes.ERROR_400, element: <BadRequest /> },
    { path: ErrorRoutes.ERROR_403, element: <Forbidden /> },
    { path: ErrorRoutes.ERROR_404, element: <NotFound /> },
    { path: ErrorRoutes.ERROR_500, element: <InternalServer /> },
    { path: ErrorRoutes.ERROR_503, element: <ServiceUnavailable /> },

    // otherRoutes
    { path: OtherRoutes.MAINTENANCE_PAGE, element: <Maintenance /> },
    { path: OtherRoutes.COMING_SOON, element: <ComingSoon /> },
  ];
  return useRoutes(element);
};

export default Routes;
